import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { LayoutGrid, List, Plus, Search } from "lucide-react";
import { useLiveQuery } from "dexie-react-hooks";
import { localDateInZone, type TaxonomyApiItem } from "@recipevault/shared";
import { useRecipes, type LibraryFilters, type LibrarySort } from "./useRecipes.js";
import { RecipeCard } from "./RecipeCard.js";
import { RecipeRow } from "./RecipeRow.js";
import { ChipFilter } from "./ChipFilter.js";
import { db } from "../../sync/db.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select.js";
import { useAuth } from "../auth/AuthProvider.js";
import { AddRecipeSheet } from "../imports/AddRecipeSheet.js";
import { useActiveImports } from "../imports/useActiveImports.js";
import { GhostRecipeCard } from "../imports/GhostRecipeCard.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { deriveShelves, matchesShelf } from "../shelves/deriveShelves.js";
import { useShelfConfig } from "../shelves/useShelfConfig.js";
import { ShelfRow } from "../shelves/ShelfRow.js";
import { ManageShelvesSheet } from "../shelves/ManageShelvesSheet.js";
import { SaveShelfDialog } from "../shelves/SaveShelfDialog.js";

// The array-valued filter keys only — maxTotalTimeMin is scalar and is
// (de)serialized separately below via the single `tmax` param, since it
// can't share FILTER_KEYS' getAll/append loop.
type ArrayFilterKey = Exclude<keyof LibraryFilters, "maxTotalTimeMin">;

// URL contract — keep keys short so the URL stays readable when shared/bookmarked.
const FILTER_KEYS: ReadonlyArray<readonly [ArrayFilterKey, string]> = [
  ["tagIds", "tag"],
  ["categoryIds", "cat"],
  ["cuisineIds", "cuisine"],
  ["dietFlags", "diet"],
];

const SORT_OPTIONS: { value: LibrarySort; label: string }[] = [
  { value: "top-rated", label: "Top rated" },
  { value: "most-cooked", label: "Most cooked" },
  { value: "recent", label: "Recently updated" },
  { value: "alpha", label: "Alphabetical" },
  { value: "rating", label: "Highest rated" },
  { value: "quickest", label: "Quickest first" },
  { value: "easiest", label: "Easiest first" },
];

function isSort(v: string | null): v is LibrarySort {
  return SORT_OPTIONS.some((o) => o.value === v);
}

// "See all" (Task 8) maps a builtin shelf to the closest existing library
// sort, so escaping a shelf into the flat view lands on a sensible order
// instead of resetting to top-rated. Shelves with no natural match (and all
// custom shelves) simply keep whatever sort was active — which, since "See
// all" is only reachable from the shelves view, is always the default
// top-rated at the moment of the click.
const SHELF_SORT_MAP: Record<string, LibrarySort> = {
  "most-cooked": "most-cooked",
  "under-30": "quickest",
  "recently-added": "recent",
};

type ViewMode = "grid" | "list";
const VIEW_STORAGE_KEY = "rv:libraryView";

/** Read persisted view mode from localStorage. Defaults to grid if unset/invalid. */
function readPersistedView(): ViewMode {
  if (typeof window === "undefined") return "grid";
  const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return raw === "list" ? "list" : "grid";
}

export function Library(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { me } = useAuth();
  const householdId = me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  // Derived state — read straight from the URL on every render. Library state
  // survives navigation away-and-back (e.g., recipe view → browser back)
  // because the URL is preserved by browser history.
  const filters = useMemo<LibraryFilters>(() => {
    const base: LibraryFilters = {
      tagIds: searchParams.getAll("tag"),
      categoryIds: searchParams.getAll("cat"),
      cuisineIds: searchParams.getAll("cuisine"),
      dietFlags: searchParams.getAll("diet"),
    };
    const tmaxRaw = searchParams.get("tmax");
    if (tmaxRaw === null) return base;
    const tmax = Number.parseInt(tmaxRaw, 10);
    // Malformed/non-positive `tmax` (hand-edited URL) is treated as absent
    // rather than crashing the filter — same "ignore, don't throw" posture
    // as isSort() below for an invalid ?sort=.
    return Number.isFinite(tmax) && tmax > 0 ? { ...base, maxTotalTimeMin: tmax } : base;
  }, [searchParams]);
  const sortRaw = searchParams.get("sort");
  const sort: LibrarySort = isSort(sortRaw) ? sortRaw : "top-rated";
  const search = searchParams.get("q") ?? "";
  // "See all" (Task 8) scopes the flat view to one shelf's full membership.
  const shelfParam = searchParams.get("shelf");

  const { recipes, status } = useRecipes({ filters, sort, search });
  // In-flight/failed imports + photo-drafts awaiting review (Plan 12, Task 10).
  // Not part of the Fuse search/sort pipeline above — ghosts always render
  // first, newest-first, independent of the active filters/search.
  const ghosts = useActiveImports();

  // Smart shelves (Plan 13, Task 8) — the library home's default browse view.
  const tz = useHouseholdTimezone();
  const today = useMemo(() => localDateInZone(tz, new Date()), [tz]);
  const { config: shelfConfig } = useShelfConfig();
  const shelves = useMemo(
    () => (shelfConfig ? deriveShelves(recipes, shelfConfig, today, tz) : []),
    [recipes, shelfConfig, today, tz],
  );
  // Hidden-inclusive derivation (hidden cleared) — the source of truth for
  // "do any shelves exist at all", independent of which ones the household
  // has hidden. Reused below to gate showShelvesView so hiding every builtin
  // shelf (no custom shelves) doesn't fall back to the flat view and strand
  // "Manage shelves" / "All shelves" unreachable.
  const allShelvesIncludingHidden = useMemo(
    () => (shelfConfig ? deriveShelves(recipes, { ...shelfConfig, hidden: [] }, today, tz) : []),
    [recipes, shelfConfig, today, tz],
  );
  // "All shelves" (below) reveals shelves that exist but are hidden — filtered
  // from the hidden-inclusive derivation above. No per-recipe filing;
  // membership stays fully derived, same as everything else here.
  const hiddenShelves = useMemo(() => {
    if (!shelfConfig || shelfConfig.hidden.length === 0) return [];
    const hiddenIds = new Set(shelfConfig.hidden);
    return allShelvesIncludingHidden.filter((s) => hiddenIds.has(s.id));
  }, [allShelvesIncludingHidden, shelfConfig]);

  const [addOpen, setAddOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(() => readPersistedView());
  const [manageOpen, setManageOpen] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [saveShelfOpen, setSaveShelfOpen] = useState(false);
  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  // One taxonomy fetch for all cards. Dexie caches; passing the map down
  // avoids N useLiveQuery subscriptions for N cards.
  const taxonomyList = useLiveQuery(
    async () => db.taxonomy.toArray(),
    [],
    [] as TaxonomyApiItem[],
  );
  const taxonomyMap = useMemo(
    () => new Map((taxonomyList ?? []).map((t) => [t.id, t])),
    [taxonomyList],
  );

  function updateParams(mutate: (p: URLSearchParams) => void): void {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    // replace: true so each keystroke / chip toggle doesn't push a history entry —
    // the user's back button should return to wherever they came from, not undo
    // every filter tweak.
    setSearchParams(next, { replace: true });
  }

  function setFilters(next: LibraryFilters): void {
    updateParams((p) => {
      for (const [, urlKey] of FILTER_KEYS) p.delete(urlKey);
      for (const [filterKey, urlKey] of FILTER_KEYS) {
        for (const id of next[filterKey]) p.append(urlKey, id);
      }
      if (next.maxTotalTimeMin != null) p.set("tmax", String(next.maxTotalTimeMin));
      else p.delete("tmax");
    });
  }

  function setSort(value: LibrarySort): void {
    updateParams((p) => {
      if (value === "top-rated") p.delete("sort");
      else p.set("sort", value);
    });
  }

  function setSearch(value: string): void {
    updateParams((p) => {
      if (value === "") p.delete("q");
      else p.set("q", value);
    });
  }

  function clearAll(): void {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  /** "See all" (Task 8): scope the flat view to one shelf's uncapped
   *  membership via `?shelf=<id>`, mapping to a matching sort where one
   *  exists (SHELF_SORT_MAP). Reachable only from the shelves view, where
   *  filters/search are guaranteed empty — so this never has to clear them. */
  function handleSeeAll(shelfId: string): void {
    updateParams((p) => {
      p.set("shelf", shelfId);
      const mapped = SHELF_SORT_MAP[shelfId];
      if (mapped !== undefined) {
        if (mapped === "top-rated") p.delete("sort");
        else p.set("sort", mapped);
      }
    });
  }

  const anyFilterActive =
    filters.tagIds.length +
      filters.categoryIds.length +
      filters.cuisineIds.length +
      filters.dietFlags.length >
      0 || filters.maxTotalTimeMin != null || search.length > 0;
  // "Save as shelf" (below) must gate on this narrower, STRUCTURED-only check,
  // not `anyFilterActive` — a ShelfFilter (see toShelfFilter in
  // SaveShelfDialog.tsx) only ever maps the 4 taxonomy arrays + maxTotalTimeMin.
  // Free-text search has no ShelfFilter equivalent, so a search-only state
  // (no chips, no time cap) must NOT offer to save: doing so would silently
  // produce an empty ShelfFilter that matches every recipe — a misleading
  // "all-recipes" shelf despite the dialog copy promising these filters.
  const hasStructuredFilter =
    filters.tagIds.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.cuisineIds.length > 0 ||
    filters.dietFlags.length > 0 ||
    filters.maxTotalTimeMin != null;
  // Shelf-scoping (via ?shelf=) counts as "actively narrowed" for the same
  // reasons chip filters/search do: it changes which empty-state applies and
  // makes "Clear filters" the right escape hatch.
  const scoped = anyFilterActive || shelfParam !== null;

  // Shelves are the library's default browse view — but ONLY when nothing
  // has narrowed the library away from that default (no search, no chip
  // filters, no shelf-scope, and the default top-rated sort), AND at least
  // one shelf actually has members *ignoring hidden* (allShelvesIncludingHidden,
  // not the visible `shelves`). A recipe that matches no smart-shelf rule
  // (e.g. an old, never-cooked, unflagged recipe) must never become
  // invisible — so an all-empty shelf set (no builtin match, no custom
  // shelves) falls through to the flat view below rather than showing a dead
  // end. But a household that has simply hidden every shelf must still land
  // on the shelves view — otherwise "Manage shelves" / "All shelves", which
  // only live inside it, become permanently unreachable.
  const wantsShelvesView = shelfParam === null && !anyFilterActive && sort === "top-rated";
  const showShelvesView = wantsShelvesView && allShelvesIncludingHidden.length > 0;

  // The flat view's recipe set: shelf-scoped when ?shelf= is present (uncapped
  // membership, filtered from the already sort/filter/search-processed
  // `recipes` so the caller's current sort order is preserved untouched),
  // otherwise the plain library-visible set.
  const visibleRecipes = useMemo(() => {
    if (!shelfParam || !shelfConfig) return recipes;
    return recipes.filter((r) => matchesShelf(r, shelfParam, shelfConfig, today, tz));
  }, [recipes, shelfParam, shelfConfig, today, tz]);

  // Ghosts count too — a lone in-flight import into an otherwise-empty
  // library still needs a reachable "Create recipe" entry point (the header
  // CTA, since the big centered empty-state CTA is suppressed once there's
  // anything — including a ghost — to show in the grid/list below).
  const showHeaderCta = status !== "ready" || recipes.length > 0 || ghosts.length > 0 || scoped;

  return (
    <>
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Library</h1>
        {showHeaderCta && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Create recipe
          </Button>
        )}
      </header>

      {/* Search + sort row */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" aria-hidden />
          <Input
            placeholder="Search recipes"
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            className="pl-9"
            aria-label="Search recipes"
          />
        </div>
        {/* Sort + view toggle share a row at every viewport — on mobile, the
            sort dropdown takes the remaining width and the toggle sits to its
            right (just icons; labels hide to save horizontal space). */}
        <div className="flex gap-2 items-center sm:w-auto">
          <Select value={sort} onValueChange={(v: string) => { if (isSort(v)) setSort(v); }}>
            <SelectTrigger className="flex-1 sm:w-48" aria-label="Sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div role="radiogroup" aria-label="View mode" className="inline-flex border border-zinc-800 rounded-md overflow-hidden shrink-0">
            <button
              type="button"
              role="radio"
              aria-checked={view === "grid"}
              aria-label="Grid view"
              onClick={() => { setView("grid"); }}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs transition-colors ${
                view === "grid" ? "bg-zinc-800 text-zinc-100" : "bg-transparent text-zinc-500 hover:text-zinc-200"
              }`}
            >
              <LayoutGrid className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={view === "list"}
              aria-label="List view"
              onClick={() => { setView("list"); }}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs transition-colors ${
                view === "list" ? "bg-zinc-800 text-zinc-100" : "bg-transparent text-zinc-500 hover:text-zinc-200"
              }`}
            >
              <List className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>
      </div>

      <ChipFilter filters={filters} onChange={setFilters} />

      {/* "Save as shelf" (Plan 13, Task 10) — reachable whenever the current
          STRUCTURED filter set (chips and/or time cap) has narrowed the
          library away from its shelves default. Deliberately narrower than
          `anyFilterActive`: free-text search has no ShelfFilter equivalent
          (see hasStructuredFilter above), so a search-only state must not
          offer to save. Membership stays derived: this only persists the
          filter set itself, never files individual recipes. */}
      {hasStructuredFilter && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-12"
            onClick={() => { setSaveShelfOpen(true); }}
          >
            Save as shelf
          </Button>
        </div>
      )}

      {/* Content */}
      {status === "loading" && <p className="text-zinc-500 text-sm">Loading…</p>}
      {/* "Nothing to show" empty states only fire when there are truly no
          cards at all — a ghost (in-flight import) counts as something to
          show, so a first import into an empty library renders the ghost
          card instead of the empty state. */}
      {status === "ready" && visibleRecipes.length === 0 && ghosts.length === 0 && !scoped && (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <div className="text-zinc-300 text-lg font-medium mb-2">Your kitchen is empty</div>
          <p className="text-zinc-500 text-sm max-w-md mb-4">
            Let&rsquo;s add your first recipe.
          </p>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Create recipe
          </Button>
        </div>
      )}
      {status === "ready" && visibleRecipes.length === 0 && ghosts.length === 0 && scoped && (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <div className="text-zinc-400 text-sm mb-3">No recipes match these filters.</div>
          <button
            type="button"
            onClick={clearAll}
            className="text-emerald-400 hover:text-emerald-300 text-sm underline underline-offset-2"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Smart shelves — the library's default browse view (Plan 13, Task 8).
          Any search/filter/shelf-scope/non-default sort falls back to the flat
          grid/list below, unchanged. */}
      {status === "ready" && showShelvesView && (
        <div className="flex min-w-0 flex-col gap-6">
          {ghosts.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {ghosts.map((g) => (
                <GhostRecipeCard key={g.jobId} ghost={g} view="grid" />
              ))}
            </div>
          )}
          {shelves.map((s) => (
            <ShelfRow key={s.id} shelf={s} householdId={householdId} taxonomy={taxonomyMap} onSeeAll={handleSeeAll} />
          ))}
          {showHidden && hiddenShelves.length > 0 && (
            <div className="flex min-w-0 flex-col gap-6">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Hidden</h2>
              {hiddenShelves.map((s) => (
                <ShelfRow key={s.id} shelf={s} householdId={householdId} taxonomy={taxonomyMap} onSeeAll={handleSeeAll} />
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-12"
              aria-pressed={showHidden}
              onClick={() => { setShowHidden((v) => !v); }}
            >
              {showHidden ? "Hide extra shelves" : "All shelves"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-12"
              onClick={() => { setManageOpen(true); }}
            >
              Manage shelves
            </Button>
          </div>
          <ManageShelvesSheet open={manageOpen} onOpenChange={setManageOpen} />
        </div>
      )}

      {status === "ready" && !showShelvesView && (ghosts.length > 0 || visibleRecipes.length > 0) && view === "grid" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {ghosts.map((g) => (
            <GhostRecipeCard key={g.jobId} ghost={g} view="grid" />
          ))}
          {visibleRecipes.map((r) => (
            <RecipeCard key={r.id} recipe={r} householdId={householdId} taxonomy={taxonomyMap} />
          ))}
        </div>
      )}
      {status === "ready" && !showShelvesView && (ghosts.length > 0 || visibleRecipes.length > 0) && view === "list" && (
        <div className="flex flex-col rounded-md overflow-hidden divide-y divide-zinc-800/0">
          {ghosts.map((g) => (
            <GhostRecipeCard key={g.jobId} ghost={g} view="list" />
          ))}
          {visibleRecipes.map((r) => (
            <RecipeRow key={r.id} recipe={r} householdId={householdId} taxonomy={taxonomyMap} />
          ))}
        </div>
      )}

      <AddRecipeSheet open={addOpen} onOpenChange={setAddOpen} />
      <SaveShelfDialog filters={filters} open={saveShelfOpen} onOpenChange={setSaveShelfOpen} />
    </>
  );
}
